import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { getProfiles } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useSelection } from "@/lib/selection";

import { AllowlistGate } from "./AllowlistGate";
import { DevBanner } from "./DevBanner";
import { type Page, Sidebar } from "./Sidebar";
import { TopBar } from "./TopBar";

/**
 * Top-level layout shell. Wraps the routed outlet with:
 *   - DevBanner (permanent, cross-environment reminder).
 *   - TopBar (brand + mode pill + avatar).
 *   - Sidebar (nav + profile A/B pickers + build info).
 *
 * Everything sits inside the AllowlistGate so an un-vetted Firebase session
 * can't render the shell. When the profile registry loads, any stale slot
 * values (e.g. a profile that no longer exists for this mode) are cleared
 * via `clearIfInvalid`.
 */
export function AppShell() {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const {
    mode,
    profileA,
    profileB,
    setMode,
    setProfileA,
    setProfileB,
    swapProfiles,
    clearIfInvalid,
  } = useSelection();
  const location = useLocation();
  const navigate = useNavigate();

  const page: Page = location.pathname.startsWith("/results") ? "results" : "search";

  const profilesQuery = useQuery({
    queryKey: ["profiles"],
    queryFn: () => getProfiles(),
    staleTime: 5 * 60_000,
  });

  // Clear cached API responses before signing out so a next-signed-in user
  // on the same browser (shared-laptop handoff) never sees the previous
  // session's /api/results, /api/profiles, or /api/modes data.
  const handleSignOut = useCallback(async () => {
    queryClient.clear();
    await auth.signOut();
  }, [auth, queryClient]);

  const modeProfiles = useMemo(
    () => (profilesQuery.data?.profiles ?? []).filter((p) => p.type === mode),
    [profilesQuery.data, mode],
  );

  // Clear any slot whose current value isn't in this mode's profile set —
  // avoids the "selected profile doesn't exist anymore" ghost after a
  // registry roll-forward.
  useEffect(() => {
    if (!profilesQuery.data) return;
    const valid = new Set(modeProfiles.map((p) => p.api_name));
    clearIfInvalid(valid);
  }, [profilesQuery.data, modeProfiles, clearIfInvalid]);

  const email = auth.user?.email ?? "";
  const displayName = auth.user?.displayName ?? null;
  const initials = initialsFromEmail(email);

  return (
    <AllowlistGate>
      <div
        style={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <DevBanner />
        <TopBar
          mode={mode}
          onModeChange={setMode}
          user={{ initials, email, displayName }}
          onSignOut={handleSignOut}
        />
        <div style={{ flex: 1, display: "flex", minHeight: 0 }}>
          <Sidebar
            page={page}
            onNav={(p) => navigate(p === "results" ? "/results" : "/search")}
            modeProfiles={modeProfiles.map((p) => ({
              api_name: p.api_name,
              display_label: p.display_label,
            }))}
            profileA={profileA}
            profileB={profileB}
            onPickA={setProfileA}
            onPickB={setProfileB}
            onSwap={swapProfiles}
            canSwap={!!profileA && !!profileB}
            buildLabel="arena@0.5.0 · europe-west1"
          />
          <main
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
              background: "var(--background)",
            }}
          >
            <Outlet />
          </main>
        </div>
      </div>
    </AllowlistGate>
  );
}

function initialsFromEmail(email: string): string {
  const local = email.split("@")[0] ?? "";
  const parts = local.split(/[._-]/).filter(Boolean);
  const joined = parts
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
  return joined || "?";
}
